import { collection, onSnapshot, orderBy, query, limit, startAfter, getDocs } from 'firebase/firestore';
import { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../FirebaseAuthConfig.js';

// SVG Paths for tab icons
const SVG_PATH_PERSON2 =
    'M98.914,23.971L177.26,23.971C188.303,23.971 193.743,20.294 193.743,12.468C193.743,-5.622 171.341,-27.828 138.068,-27.828C104.846,-27.828 82.444,-5.622 82.444,12.468C82.444,20.294 87.884,23.971 98.914,23.971ZM98.144,11.915C96.561,11.915 96.01,11.399 96.01,10.237C96.01,1.027 111.375,-15.772 138.068,-15.772C164.812,-15.772 180.14,1.027 180.14,10.237C180.14,11.399 179.627,11.915 178.03,11.915L98.144,11.915ZM138.132,-34.816C153.56,-34.816 165.944,-48.273 165.944,-64.973C165.944,-81.293 153.453,-94.497 138.132,-94.497C122.812,-94.497 110.296,-81.166 110.296,-64.897C110.296,-48.235 122.68,-34.816 138.132,-34.816ZM138.132,-46.834C129.723,-46.834 122.731,-54.685 122.731,-64.926C122.731,-74.716 129.75,-82.442 138.132,-82.442C146.544,-82.442 153.509,-74.805 153.509,-64.973C153.509,-54.79 146.571,-46.834 138.132,-46.834ZM30.175,24L79.106,24C75.344,21.753 73.108,16.567 73.498,12.159L30.649,12.159C29.352,12.159 28.852,11.565 28.852,10.455C28.852,-3.018 44.999,-16.094 64.557,-16.094C71.005,-16.094 78.273,-14.368 83.159,-11.629C85.289,-15.08 88.151,-18.066 92.081,-20.549C84.212,-25.22 73.93,-27.877 64.557,-27.877C37.555,-27.877 16.084,-8.83 16.084,11.878C16.084,19.81 20.77,24 30.175,24ZM64.621,-33.905C78.078,-33.905 88.914,-45.715 88.914,-60.451C88.914,-74.712 78.009,-86.367 64.621,-86.367C51.28,-86.367 40.317,-74.555 40.317,-60.375C40.317,-45.648 51.178,-33.905 64.621,-33.905ZM64.621,-45.768C57.829,-45.768 52.18,-52.132 52.18,-60.375C52.18,-68.164 57.865,-74.504 64.621,-74.504C71.386,-74.504 77.089,-68.253 77.089,-60.451C77.089,-52.132 71.442,-45.768 64.621,-45.768Z';
const SVG_PATH_PERSON =
    'M43.837,259.2l171.498,0c24.161,0 35.98,-8.046 35.98,-25.168c0,-39.581 -48.931,-88.165 -121.73,-88.165c-72.687,0 -121.701,48.584 -121.701,88.165c-0,17.122 11.873,25.168 35.952,25.168Zm-1.603,-26.377c-3.463,0 -4.669,-1.131 -4.669,-3.673c0,-20.15 33.535,-56.906 92.021,-56.906c58.514,0 92.051,36.756 92.051,56.906c0,2.542 -1.206,3.673 -4.7,3.673l-174.703,0Zm87.41,-102.246c33.837,0 60.851,-29.442 60.851,-65.98c0,-35.709 -27.247,-64.598 -60.851,-64.598c-33.437,0 -60.822,29.167 -60.822,64.764c0,36.455 27.096,65.814 60.822,65.814Zm0,-26.294c-18.315,0 -33.697,-16.666 -33.697,-39.584c0,-21.931 15.41,-38.323 33.697,-38.323c18.404,0 33.726,16.197 33.726,38.221c0,22.854 -15.18,39.686 -33.726,39.686Z';

/**
 * Combined Leaderboard Component
 *
 * Features:
 * - Tab-based UI switching between Team Rank and Individual
 * - Infinite scroll pagination (loads more when scrolling to bottom)
 * - Teams show: rank, name, total points, total steps
 * - Individuals show: rank (or profile pic), name, total points
 */

// Number of entries to load per batch - adjust this as needed
const BATCH_SIZE = 10;

// Assign place and isTied for individuals (same points => same place, isTied true when >1 share that place)
function assignPlacesWithTies(individuals) {
    let currentPlace = 1;
    const withPlace = individuals.map((ind, index) => {
        const prevPoints = index === 0 ? undefined : individuals[index - 1].points;
        if (prevPoints !== undefined && ind.points === prevPoints) {
            // same place as previous
        } else {
            currentPlace = index + 1;
        }
        return { ...ind, place: currentPlace };
    });
    const placeCounts = {};
    withPlace.forEach((r) => {
        placeCounts[r.place] = (placeCounts[r.place] || 0) + 1;
    });
    return withPlace.map((r) => ({ ...r, isTied: placeCounts[r.place] > 1 }));
}

function LeaderboardBlock({ className }) {
    // Tab state: 'team' or 'individual'
    const [activeTab, setActiveTab] = useState('team');

    // Team leaderboard state
    const [teamLeaderboard, setTeamLeaderboard] = useState([]);
    const [teamLastDoc, setTeamLastDoc] = useState(null);
    const [teamHasMore, setTeamHasMore] = useState(true);
    const [teamLoading, setTeamLoading] = useState(false);

    // Individual leaderboard state
    const [individualLeaderboard, setIndividualLeaderboard] = useState([]);
    const [individualLastDoc, setIndividualLastDoc] = useState(null);
    const [individualHasMore, setIndividualHasMore] = useState(true);
    const [individualLoading, setIndividualLoading] = useState(false);

    // Scroll container ref for infinite scroll detection
    const scrollContainerRef = useRef(null);

    // Initial load for teams
    useEffect(() => {
        const unsubscribe = onSnapshot(
            query(collection(db, '/challengeCollection/everydayWarship/teams/'), orderBy('points', 'desc'), orderBy('steps', 'desc'), limit(BATCH_SIZE)),
            (snapshot) => {
                const teams = snapshot.docs.map((doc, index) => ({
                    ...doc.data(),
                    id: doc.id,
                    place: index + 1,
                    docRef: doc,
                }));
                setTeamLeaderboard(teams);
                setTeamLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
                setTeamHasMore(snapshot.docs.length === BATCH_SIZE);
            },
            (error) => {
                console.error('Team leaderboard error:', error);
                setTeamLeaderboard([]);
            },
        );

        return () => unsubscribe();
    }, []);

    // Initial load for individuals
    useEffect(() => {
        const unsubscribe = onSnapshot(
            query(collection(db, '/challengeCollection/everydayWarship/individualLeaderboard/'), orderBy('points', 'desc'), limit(BATCH_SIZE)),
            (snapshot) => {
                const raw = snapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                    docRef: doc,
                }));
                setIndividualLeaderboard(assignPlacesWithTies(raw));
                setIndividualLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
                setIndividualHasMore(snapshot.docs.length === BATCH_SIZE);
            },
            (error) => {
                console.error('Individual leaderboard error:', error);
                setIndividualLeaderboard([]);
            },
        );

        return () => unsubscribe();
    }, []);

    // Load more teams
    const loadMoreTeams = useCallback(async () => {
        if (!teamHasMore || teamLoading || !teamLastDoc) return;

        setTeamLoading(true);
        try {
            const snapshot = await getDocs(
                query(
                    collection(db, '/challengeCollection/everydayWarship/teams/'),
                    orderBy('points', 'desc'),
                    orderBy('steps', 'desc'),
                    startAfter(teamLastDoc),
                    limit(BATCH_SIZE),
                ),
            );

            const newTeams = snapshot.docs.map((doc, index) => ({
                ...doc.data(),
                id: doc.id,
                place: teamLeaderboard.length + index + 1,
                docRef: doc,
            }));

            setTeamLeaderboard((prev) => [...prev, ...newTeams]);
            setTeamLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
            setTeamHasMore(snapshot.docs.length === BATCH_SIZE);
        } catch (error) {
            console.error('Error loading more teams:', error);
        }
        setTeamLoading(false);
    }, [teamHasMore, teamLoading, teamLastDoc, teamLeaderboard.length]);

    // Load more individuals
    const loadMoreIndividuals = useCallback(async () => {
        if (!individualHasMore || individualLoading || !individualLastDoc) return;

        setIndividualLoading(true);
        try {
            const snapshot = await getDocs(
                query(
                    collection(db, '/challengeCollection/everydayWarship/individualLeaderboard/'),
                    orderBy('points', 'desc'),
                    startAfter(individualLastDoc),
                    limit(BATCH_SIZE),
                ),
            );

            const newIndividuals = snapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
                docRef: doc,
            }));

            setIndividualLeaderboard((prev) => assignPlacesWithTies([...prev, ...newIndividuals]));
            setIndividualLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
            setIndividualHasMore(snapshot.docs.length === BATCH_SIZE);
        } catch (error) {
            console.error('Error loading more individuals:', error);
        }
        setIndividualLoading(false);
    }, [individualHasMore, individualLoading, individualLastDoc, individualLeaderboard.length]);

    // Handle scroll for infinite loading
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

        if (isNearBottom) {
            if (activeTab === 'team') {
                loadMoreTeams();
            } else {
                loadMoreIndividuals();
            }
        }
    }, [activeTab, loadMoreTeams, loadMoreIndividuals]);

    // Get rank badge color based on position
    const getRankColor = (place) => {
        switch (place) {
            case 1:
                return 'bg-[#E5C97F]'; // Gold
            case 2:
                return 'bg-[#9FABB9]'; // Silver
            case 3:
                return 'bg-[#CD7F32]'; // Bronze (updated to more bronze-like)
            default:
                return 'bg-neutral-600'; // Gray
        }
    };

    // Format number with commas
    const formatNumber = (num) => {
        return num?.toLocaleString() || '0';
    };

    // Render rank badge (circle with number)
    const RankBadge = ({ place }) => (
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRankColor(place)}`}>
            <span className="font-sans text-sm font-bold text-white">{place}</span>
        </div>
    );

    // Render team row
    const TeamRow = ({ team }) => (
        <div className="w-full py-3 px-2 flex flex-row items-center justify-between border-b border-neutral-300 dark:border-neutral-600 last:border-b-0">
            <div className="flex flex-row items-center gap-3">
                <RankBadge place={team.place} />
                <div className="flex flex-col">
                    <span className="font-sans text-sm font-semibold text-neutral-800 dark:text-white">{team.name}</span>
                    <span className="font-sans text-xs text-neutral-500 dark:text-neutral-400">{formatNumber(team.steps || 0)} steps</span>
                </div>
            </div>
            <span className="font-sans text-lg font-bold text-[#FF671F]">
                {formatNumber(team.points)} {team.points === 1 ? 'Point' : 'Points'}
            </span>
        </div>
    );

    // Get ordinal suffix (1st, 2nd, 3rd, etc.)
    const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    // Render individual row
    const IndividualRow = ({ individual }) => (
        <div className="w-full py-3 px-2 flex flex-row items-center justify-between border-b border-neutral-300 dark:border-neutral-600 last:border-b-0">
            <div className="flex flex-row items-center gap-3">
                {individual.profilePic ? (
                    <img
                        className="w-10 h-10 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                        src={individual.profilePic}
                        alt={individual.displayName}
                    />
                ) : (
                    <RankBadge place={individual.place} />
                )}
                <div className="flex flex-col">
                    <span className="font-sans text-sm font-semibold text-neutral-800 dark:text-white">{individual.displayName}</span>
                    <span className="font-sans text-xs text-neutral-500 dark:text-neutral-400">
                        {individual.isTied ? `Tied for ${getOrdinal(individual.place)} Place` : `${getOrdinal(individual.place)} Place`}
                    </span>
                </div>
            </div>
            <span className="font-sans text-lg font-bold text-[#FF671F]">
                {formatNumber(individual.points)} {individual.points === 1 ? 'Point' : 'Points'}
            </span>
        </div>
    );

    return (
        <div className={`w-full rounded-2xl bg-neutral-200 dark:bg-neutral-700 overflow-hidden flex flex-col p-4 ${className || ''}`}>
            {/* Header */}
            <div className="w-full pb-2">
                <h2 className="font-sans text-xl font-bold text-neutral-700 dark:text-neutral-200">Leaderboards</h2>
            </div>

            {/* Tab Header */}
            <div className="w-full flex flex-row border-b border-neutral-300 dark:border-neutral-600">
                {/* Team Rank Tab */}
                <button
                    className="flex-1 py-3 px-4 flex flex-row items-center justify-center gap-2 border-none cursor-pointer transition-all duration-200 bg-transparent"
                    onClick={() => setActiveTab('team')}
                    style={{ borderBottomWidth: '2px', borderBottomStyle: 'solid', borderBottomColor: activeTab === 'team' ? '#FF671F' : 'transparent' }}
                >
                    <svg
                        className={`w-4 h-4 fill-current ${activeTab === 'team' ? 'text-[#FF671F]' : 'text-neutral-500 dark:text-neutral-400'}`}
                        viewBox="16 -96 180 122"
                    >
                        <path d={SVG_PATH_PERSON2} />
                    </svg>
                    <span
                        className={`font-sans text-sm font-bold uppercase tracking-wide ${
                            activeTab === 'team' ? 'text-[#FF671F]' : 'text-neutral-500 dark:text-neutral-400'
                        }`}
                    >
                        Team Rank
                    </span>
                </button>

                {/* Individual Tab */}
                <button
                    className="flex-1 py-3 px-4 flex flex-row items-center justify-center gap-2 border-none cursor-pointer transition-all duration-200 bg-transparent"
                    onClick={() => setActiveTab('individual')}
                    style={{ borderBottomWidth: '2px', borderBottomStyle: 'solid', borderBottomColor: activeTab === 'individual' ? '#FF671F' : 'transparent' }}
                >
                    <svg
                        className={`w-4 h-4 fill-current ${activeTab === 'individual' ? 'text-[#FF671F]' : 'text-neutral-500 dark:text-neutral-400'}`}
                        viewBox="0 0 260 260"
                    >
                        <path d={SVG_PATH_PERSON} />
                    </svg>
                    <span
                        className={`font-sans text-sm font-bold uppercase tracking-wide ${
                            activeTab === 'individual' ? 'text-[#FF671F]' : 'text-neutral-500 dark:text-neutral-400'
                        }`}
                    >
                        Individual
                    </span>
                </button>
            </div>

            {/* Scrollable Content - constrained height enables infinite scroll */}
            <div ref={scrollContainerRef} className="w-full flex-1 overflow-y-auto px-2 pb-2 max-h-[300px] md:max-h-[375px] min-h-0" onScroll={handleScroll}>
                {activeTab === 'team' ? (
                    <>
                        {teamLeaderboard.length === 0 ? (
                            <div className="w-full py-8 flex justify-center">
                                <span className="font-sans text-sm text-neutral-500 dark:text-neutral-400">No teams yet</span>
                            </div>
                        ) : (
                            teamLeaderboard.map((team) => <TeamRow key={team.id} team={team} />)
                        )}
                        {teamLoading && (
                            <div className="w-full py-4 flex justify-center">
                                <span className="font-sans text-sm text-neutral-500 dark:text-neutral-400">Loading...</span>
                            </div>
                        )}
                        {!teamHasMore && teamLeaderboard.length > 0 && (
                            <div className="w-full py-4 flex justify-center">
                                <span className="font-sans text-xs text-neutral-400 dark:text-neutral-500">End of leaderboard</span>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {individualLeaderboard.length === 0 ? (
                            <div className="w-full py-8 flex justify-center">
                                <span className="font-sans text-sm text-neutral-500 dark:text-neutral-400">No individuals yet</span>
                            </div>
                        ) : (
                            individualLeaderboard.map((individual) => <IndividualRow key={individual.id} individual={individual} />)
                        )}
                        {individualLoading && (
                            <div className="w-full py-4 flex justify-center">
                                <span className="font-sans text-sm text-neutral-500 dark:text-neutral-400">Loading...</span>
                            </div>
                        )}
                        {!individualHasMore && individualLeaderboard.length > 0 && (
                            <div className="w-full py-4 flex justify-center">
                                <span className="font-sans text-xs text-neutral-400 dark:text-neutral-500">End of leaderboard</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default LeaderboardBlock;
